/* eslint-disable */
/* prettier-ignore */
// Generated by elegant-router
// Read more: https://github.com/soybeanjs/elegant-router

import type { RouteRecordRaw, RouteComponent } from 'vue-router';
import type { ElegantConstRoute } from '@elegant-router/vue';
import type { RouteMap, RouteKey, RoutePath } from '@elegant-router/types';

/**
 * transform elegant const routes to vue routes
 * @param routes elegant const routes
 * @param layouts layout components
 * @param views view components
 */
export function transformElegantRoutesToVueRoutes(
  routes: ElegantConstRoute[],
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  return routes.flatMap(route => transformElegantRouteToVueRoute(route, layouts, views));
}

/**
 * transform elegant route to vue route
 * @param route elegant const route
 * @param layouts layout components
 * @param views view components
 */
function transformElegantRouteToVueRoute(
  route: ElegantConstRoute,
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  const LAYOUT_PREFIX = 'layout.';
  const VIEW_PREFIX = 'view.';
  const ROUTE_DEGREE_SPLITTER = '_';
  const FIRST_LEVEL_ROUTE_COMPONENT_SPLIT = '$';

  function isLayout(component: string) {
    return component.startsWith(LAYOUT_PREFIX);
  }

  function getLayoutName(component: string) {
    const layout = component.replace(LAYOUT_PREFIX, '');

    if(!layouts[layout]) {
      throw new Error(`Layout component "${layout}" not found`);
    }

    return layout;
  }

  function isView(component: string) {
    return component.startsWith(VIEW_PREFIX);
  }

  function getViewName(component: string) {
    const view = component.replace(VIEW_PREFIX, '');

    if(!views[view]) {
      throw new Error(`View component "${view}" not found`);
    }

    return view;
  }

  function isFirstLevelRoute(item: ElegantConstRoute) {
    return !item.name.includes(ROUTE_DEGREE_SPLITTER);
  }

  function isSingleLevelRoute(item: ElegantConstRoute) {
    return isFirstLevelRoute(item) && !item.children?.length;
  }

  function getSingleLevelRouteComponent(component: string) {
    const [layout, view] = component.split(FIRST_LEVEL_ROUTE_COMPONENT_SPLIT);

    return {
      layout: getLayoutName(layout),
      view: getViewName(view)
    };
  }

  const vueRoutes: RouteRecordRaw[] = [];

  // add props: true to route
  if (route.path.includes(':') && !route.props) {
    route.props = true;
  }

  const { name, path, component, children, ...rest } = route;

  const vueRoute = { name, path, ...rest } as RouteRecordRaw;

  try {
    if (component) {
      if (isSingleLevelRoute(route)) {
        const { layout, view } = getSingleLevelRouteComponent(component);

        const singleLevelRoute: RouteRecordRaw = {
          path,
          component: layouts[layout],
          meta: {
            title: route.meta?.title || ''
          },
          children: [
            {
              name,
              path: '',
              component: views[view],
              ...rest
            } as RouteRecordRaw
          ]
        };

        return [singleLevelRoute];
      }

      if (isLayout(component)) {
        const layoutName = getLayoutName(component);

        vueRoute.component = layouts[layoutName];
      }

      if (isView(component)) {
        const viewName = getViewName(component);

        vueRoute.component = views[viewName];
      }

    }
  } catch (error: any) {
    console.error(`Error transforming route "${route.name}": ${error.toString()}`);
    return [];
  }

  // add redirect to child
  if (children?.length && !vueRoute.redirect) {
    vueRoute.redirect = {
      name: children[0].name
    };
  }

  if (children?.length) {
    const childRoutes = children.flatMap(child => transformElegantRouteToVueRoute(child, layouts, views));

    if(isFirstLevelRoute(route)) {
      vueRoute.children = childRoutes;
    } else {
      vueRoutes.push(...childRoutes);
    }
  }

  vueRoutes.unshift(vueRoute);

  return vueRoutes;
}

/**
 * map of route name and route path
 */
const routeMap: RouteMap = {
  "root": "/",
  "not-found": "/:pathMatch(.*)*",
  "exception": "/exception",
  "exception_403": "/exception/403",
  "exception_404": "/exception/404",
  "exception_500": "/exception/500",
  "403": "/403",
  "404": "/404",
  "500": "/500",
  "appointment": "/appointment",
  "appointment_appointment-audit": "/appointment/appointment-audit",
  "appointment_appointment-distribution": "/appointment/appointment-distribution",
  "appointment_appointment-info": "/appointment/appointment-info",
  "appointment_appointment-loss": "/appointment/appointment-loss",
  "attachment": "/attachment",
  "base": "/base",
  "base_product-info": "/base/product-info",
  "base_product-sku": "/base/product-sku",
  "base_product-spu": "/base/product-spu",
  "base_vehicle-brand": "/base/vehicle-brand",
  "base_vehicle-info": "/base/vehicle-info",
  "base_vehicle-oem": "/base/vehicle-oem",
  "base_vehicle-series": "/base/vehicle-series",
  "base_warehouse": "/base/warehouse",
  "certified-technician": "/certified-technician",
  "customer-info": "/customer-info",
  "display-case": "/display-case",
  "finance": "/finance",
  "finance_film-quotation": "/finance/film-quotation",
  "finance_finance-commission-details": "/finance/finance-commission-details",
  "finance_finance-sale-plan": "/finance/finance-sale-plan",
  "finance_finance-transaction-record": "/finance/finance-transaction-record",
  "finance_finance-wallet": "/finance/finance-wallet",
  "finance_partner-commission-record": "/finance/partner-commission-record",
  "finance_sale": "/finance/sale",
  "finance_sale_order": "/finance/sale/order",
  "finance_sale_order_commission": "/finance/sale/order/commission",
  "finance_sale_order_commission_record": "/finance/sale/order/commission/record",
  "finance_sale-order-technician-commission": "/finance/sale-order-technician-commission",
  "general-form": "/general-form/:config",
  "home": "/home",
  "iframe-page": "/iframe-page/:url",
  "inventory": "/inventory",
  "inventory_circulating-inventory-info": "/inventory/circulating-inventory-info",
  "inventory_inventory-allocated-record": "/inventory/inventory-allocated-record",
  "inventory_inventory-check-record": "/inventory/inventory-check-record",
  "inventory_inventory-info": "/inventory/inventory-info",
  "inventory_sales-record-info": "/inventory/sales-record-info",
  "inventory_store-inventory-info": "/inventory/store-inventory-info",
  "login": "/login/:module(pwd-login)?",
  "message": "/message",
  "order": "/order",
  "order_film-purchase-credit": "/order/film-purchase-credit",
  "order_film-purchase-order-payment-log": "/order/film-purchase-order-payment-log",
  "order_order-evaluation": "/order/order-evaluation",
  "order_order-info": "/order/order-info",
  "order_store-outbound-order": "/order/store-outbound-order",
  "order_store-purchase-order": "/order/store-purchase-order",
  "order_store-sale-order": "/order/store-sale-order",
  "order_store-sale-order-payment-log": "/order/store-sale-order-payment-log",
  "order_store-warranty-order": "/order/store-warranty-order",
  "report-platform": "/report-platform",
  "report-platform_customer-distribution": "/report-platform/customer-distribution",
  "report-platform_inventory-statistics": "/report-platform/inventory-statistics",
  "report-platform_order-info": "/report-platform/order-info",
  "report-platform_product-sale-ranking": "/report-platform/product-sale-ranking",
  "report-platform_sales-amount": "/report-platform/sales-amount",
  "report-platform_sales-analysis": "/report-platform/sales-analysis",
  "report-store": "/report-store",
  "report-store_purchase-order-credit": "/report-store/purchase-order-credit",
  "report-store_purchase-order-credit-settlement": "/report-store/purchase-order-credit-settlement",
  "report-store_sale-order-credit": "/report-store/sale-order-credit",
  "report-store_sale-order-credit-settlement": "/report-store/sale-order-credit-settlement",
  "report-store_statistics-customer-info": "/report-store/statistics-customer-info",
  "report-store_store-employee-month-commission": "/report-store/store-employee-month-commission",
  "report-store_store-sale-order-commission": "/report-store/store-sale-order-commission",
  "report-store_transaction-records": "/report-store/transaction-records",
  "report-store_view-statistics-appointment-loss": "/report-store/view-statistics-appointment-loss",
  "report-store_view-statistics-non-upgrade": "/report-store/view-statistics-non-upgrade",
  "sale-tools": "/sale-tools",
  "sale-tools_st-category-quick": "/sale-tools/st-category-quick",
  "sale-tools_st-category-section": "/sale-tools/st-category-section",
  "sale-tools_st-category-section-share": "/sale-tools/st-category-section-share",
  "sale-tools_st-cm-mod-item": "/sale-tools/st-cm-mod-item",
  "sale-tools_st-cm-vehicle-zones": "/sale-tools/st-cm-vehicle-zones",
  "sale-tools_st-solution": "/sale-tools/st-solution",
  "sale-tools_st-survey-answer": "/sale-tools/st-survey-answer",
  "sale-tools_st-survey-option": "/sale-tools/st-survey-option",
  "sale-tools_st-survey-question": "/sale-tools/st-survey-question",
  "sale-tools_st-survey-quick": "/sale-tools/st-survey-quick",
  "sale-tools_st-survey-share": "/sale-tools/st-survey-share",
  "sale-tools_st-user-tracking": "/sale-tools/st-user-tracking",
  "sale-tools_store-info-edit": "/sale-tools/store-info-edit",
  "school": "/school",
  "school_lecture-category": "/school/lecture-category",
  "school_lecture-course": "/school/lecture-course",
  "school_study-record": "/school/study-record",
  "statistics": "/statistics",
  "statistics_factory-sales-statistics": "/statistics/factory-sales-statistics",
  "store": "/store",
  "store_circulation-info": "/store/circulation-info",
  "store_commission-rule": "/store/commission-rule",
  "store_commission-rule-partner": "/store/commission-rule-partner",
  "store_commission-rule-product-info": "/store/commission-rule-product-info",
  "store_commission-rule-product-sku": "/store/commission-rule-product-sku",
  "store_commission-rule-sale-price": "/store/commission-rule-sale-price",
  "store_film-purchase-order": "/store/film-purchase-order",
  "store_material-item": "/store/material-item",
  "store_material-transaction": "/store/material-transaction",
  "store_material-type": "/store/material-type",
  "store_member-info": "/store/member-info",
  "store_member-service-package": "/store/member-service-package",
  "store_member-vehicle-info": "/store/member-vehicle-info",
  "store_service-item": "/store/service-item",
  "store_service-item-template": "/store/service-item-template",
  "store_service-package": "/store/service-package",
  "store_service-type": "/store/service-type",
  "store_service-type-template": "/store/service-type-template",
  "store_store-audit-record": "/store/store-audit-record",
  "store_store-customer": "/store/store-customer",
  "store_store-info": "/store/store-info",
  "store_store-sale-order-circulation": "/store/store-sale-order-circulation",
  "store_store-supplier-info": "/store/store-supplier-info",
  "sys": "/sys",
  "sys_account": "/sys/account",
  "sys_biz-attachment-type": "/sys/biz-attachment-type",
  "sys_data-filter": "/sys/data-filter",
  "sys_department": "/sys/department",
  "sys_dict": "/sys/dict",
  "sys_dict-extra": "/sys/dict-extra",
  "sys_gen-entity": "/sys/gen-entity",
  "sys_menu": "/sys/menu",
  "sys_miniapp": "/sys/miniapp",
  "sys_role": "/sys/role",
  "user-center": "/user-center"
};

/**
 * get route path by route name
 * @param name route name
 */
export function getRoutePath<T extends RouteKey>(name: T) {
  return routeMap[name];
}

/**
 * get route name by route path
 * @param path route path
 */
export function getRouteName(path: RoutePath) {
  const routeEntries = Object.entries(routeMap) as [RouteKey, RoutePath][];

  const routeName: RouteKey | null = routeEntries.find(([, routePath]) => routePath === path)?.[0] || null;

  return routeName;
}
